import { ValueGetterParams } from 'ag-grid-community'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../../domain/entity/WbsItemEntity'
import {
  ProgramManagementGridContext,
  ProgramManagementProgress,
  ProgramManagementRow,
} from '../../../programManagement'

type ProductivityMetric = 'sv' | 'spi' | 'cv' | 'cpi' | 'etc' | 'eac' | 'vac'

/** context の aggregateTarget / aggregateField に応じた進捗データを返す */
const getActiveProgress = (
  params: ValueGetterParams<ProgramManagementRow>
): ProgramManagementProgress | null => {
  const progress = params.data?.progress
  if (!progress) return null

  const ctx = params.context as ProgramManagementGridContext | undefined
  const isTask = ctx?.aggregateTarget === WbsItemType.TASK
  const isCount = !ctx || ctx.aggregateField === AggregateField.WBS_ITEM_COUNT

  // 報告の入力単位に応じてサブオブジェクトは null/欠落となる（未報告→空欄表示）
  if (isTask) {
    return (isCount ? progress.taskCount : progress.taskWorkload) ?? null
  }
  return (
    (isCount ? progress.deliverableCount : progress.deliverableWorkload) ?? null
  )
}

/**
 * ネストされたデータから値を取得する。
 * progress カテゴリの場合は context に応じたバリエーションを選択する。
 */
export const getAggregatedValue = (
  params: ValueGetterParams<ProgramManagementRow>,
  category: 'progress' | 'resource' | 'profitLoss',
  field: string
): number | null => {
  const data = params.data
  if (!data) return null

  if (category === 'progress') {
    const progress = getActiveProgress(params)
    if (!progress) return null
    const value = (progress as Record<string, unknown>)[field]
    return typeof value === 'number' ? value : null
  }

  const categoryData = data[category]
  if (!categoryData) return null
  const value = (categoryData as Record<string, unknown>)[field]
  return typeof value === 'number' ? value : null
}

/**
 * 生産性指標の計算には常に工数(workload)ベースの進捗を使用する。
 * actualResource（sumActualHour）が時間単位のため、
 * EV/PV/BAC も同じ時間単位の工数進捗値を直接使用する。
 */
const getWorkloadProgress = (
  params: ValueGetterParams<ProgramManagementRow>
): ProgramManagementProgress | null => {
  const progress = params.data?.progress
  if (!progress) return null

  const ctx = params.context as ProgramManagementGridContext | undefined
  const isTask = ctx?.aggregateTarget === WbsItemType.TASK
  // 工数未報告（個数のみ報告）の場合は null（生産性指標は空欄表示）
  return (isTask ? progress.taskWorkload : progress.deliverableWorkload) ?? null
}

/**
 * 進捗とリソースから EVM 生産性指標を算出する。
 *
 * EV = completed（完了工数: 時間）
 * PV = scheduledCompleted（予定完了工数: 時間）
 * AC = productivity.actualHour（投入工数: 時間）
 * BAC = total（総工数: 時間）
 */
export const calculateProductivity = (
  params: ValueGetterParams<ProgramManagementRow>,
  metric: ProductivityMetric
): number | null => {
  const data = params.data
  if (!data) return null

  const progress = getWorkloadProgress(params)
  if (!progress) return null

  const { total, scheduledCompleted, completed } = progress

  const ev = completed
  const pv = scheduledCompleted
  const bac = total
  // AC（投入工数）: 行ごとに採番済みの productivity.actualHour を参照する。
  // PROJECT/工程行はサーバーが、PROGRAM/GROUP はフロントの集計が値を入れる。
  const ac = data.productivity?.actualHour ?? null
  const cpi = ac == null || ac === 0 ? null : ev / ac

  switch (metric) {
    case 'sv':
      return ev - pv
    case 'spi':
      return pv === 0 ? null : ev / pv
    case 'cv':
      return ac == null ? null : ev - ac
    case 'cpi':
      return cpi
    case 'etc':
      return !cpi ? null : (bac - ev) / cpi
    case 'eac':
      return !cpi ? null : bac / cpi
    case 'vac': {
      // VAC = BAC - EAC（プロジェクト計画の Progress.varianceAtCompletion と同式。
      // EAC が算出不能または 0 の場合は非表示）
      const eac = !cpi ? null : bac / cpi
      return !eac ? null : bac - eac
    }
    default:
      return null
  }
}
